import { useEffect, useMemo, useState } from "react";
import { Calendar, Loader2, Sparkles, FileText, Check, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError } from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";

const STATUS_COLORS = {
  planned: { bg: "#71717A", label: "Planned" },
  drafted: { bg: "#0055FF", label: "Drafted" },
  published: { bg: "#10B981", label: "Published" },
  skipped: { bg: "#EF4444", label: "Skipped" },
};

export default function ContentPlanPage() {
  const { active } = useProjects();
  const [plans, setPlans] = useState([]);
  const [activePlan, setActivePlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [days, setDays] = useState(30);
  const [postsPerWeek, setPostsPerWeek] = useState(3);

  const refresh = async () => {
    if (!active) return;
    const { data } = await api.get(`/content-plan?project_id=${active.id}`);
    setPlans(data);
    if (data.length > 0 && !activePlan) setActivePlan(data[0]);
  };

  useEffect(() => { refresh(); }, [active?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const generate = async () => {
    if (!active) {
      setError("Please activate a project first.");
      return;
    }
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/content-plan", {
        project_id: active.id, days, posts_per_week: postsPerWeek,
      });
      setActivePlan(data);
      refresh();
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const updateStatus = async (planId, postId, status) => {
    await api.patch(`/content-plan/${planId}/posts/${postId}?status=${status}`).catch(() => {});
    setActivePlan((prev) => prev ? {
      ...prev,
      posts: prev.posts.map((p) => p.id === postId ? { ...p, status } : p),
    } : prev);
  };

  // Group posts by week
  const grouped = useMemo(() => {
    if (!activePlan) return [];
    const out = [];
    activePlan.posts.forEach((p) => {
      const w = Math.floor(((p.day || 1) - 1) / 7) + 1;
      let bucket = out.find((b) => b.week === w);
      if (!bucket) { bucket = { week: w, posts: [] }; out.push(bucket); }
      bucket.posts.push(p);
    });
    out.forEach((b) => b.posts.sort((a, c) => (a.day || 0) - (c.day || 0)));
    return out;
  }, [activePlan]);

  return (
    <DashboardLayout
      eyebrow="Content Calendar"
      title="30-day publishing plan."
      subtitle="AI maps an entire month of articles by topic, intent and publish date."
    >
      {!active ? (
        <ProjectGate />
      ) : (
        <>
          {/* Form */}
          <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-6 mb-6" data-testid="content-plan-form">
            <div className="grid md:grid-cols-[1fr_180px_180px_180px] gap-3 items-end">
              <div>
                <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Project</Label>
                <div className="h-11 px-3 bg-zinc-900 border border-zinc-800 rounded-sm flex items-center text-zinc-200">{active.name} · <span className="text-zinc-500 ml-2">{active.domain}</span></div>
              </div>
              <div>
                <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Days</Label>
                <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v))}>
                  <SelectTrigger className="h-11 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="plan-days-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0B0B0E] border-zinc-800 rounded-sm">
                    <SelectItem value="14">14 days</SelectItem>
                    <SelectItem value="30">30 days</SelectItem>
                    <SelectItem value="60">60 days</SelectItem>
                    <SelectItem value="90">90 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs tracking-[0.18em] uppercase text-zinc-500 mb-2 block">Posts / week</Label>
                <Select value={String(postsPerWeek)} onValueChange={(v) => setPostsPerWeek(parseInt(v))}>
                  <SelectTrigger className="h-11 bg-zinc-950 border-zinc-800 rounded-sm" data-testid="plan-pace-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0B0B0E] border-zinc-800 rounded-sm">
                    <SelectItem value="1">1 / week</SelectItem>
                    <SelectItem value="2">2 / week</SelectItem>
                    <SelectItem value="3">3 / week</SelectItem>
                    <SelectItem value="5">5 / week</SelectItem>
                    <SelectItem value="7">Daily</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={generate} disabled={loading} className="h-11 bg-[#0055FF] hover:bg-[#3377FF] rounded-sm" data-testid="plan-generate-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-2" /> Generate plan</>}
              </Button>
            </div>
            {error && <div className="mt-3 text-sm text-red-400" data-testid="plan-error">{error}</div>}
            {plans.length > 0 && (
              <div className="mt-4 pt-4 border-t border-zinc-800 flex items-center gap-2 flex-wrap">
                <span className="label-eyebrow">Saved plans:</span>
                {plans.map((p) => (
                  <button key={p.id} onClick={() => setActivePlan(p)}
                          className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${
                            activePlan?.id === p.id ? "bg-[#0055FF]/10 border-[#0055FF] text-white" : "bg-zinc-900 border-zinc-800 text-zinc-400 hover:text-white"
                          }`}
                          data-testid={`plan-tab-${p.id}`}>
                    {p.days}d · {new Date(p.created_at).toLocaleDateString()}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Plan view */}
          {loading && (
            <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-12 text-center">
              <Loader2 className="w-8 h-8 mx-auto text-[#0055FF] animate-spin mb-3" />
              <p className="text-zinc-400">Generating your calendar — this takes ~20 seconds...</p>
            </div>
          )}

          {activePlan && !loading && (
            <div className="space-y-6 fade-in" data-testid="content-plan-result">
              <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-6">
                <span className="label-eyebrow">Strategic rationale</span>
                <p className="text-zinc-300 mt-3 leading-relaxed">{activePlan.summary}</p>
                <div className="mt-4 flex items-center gap-6 text-sm">
                  <Stat label="Posts" value={activePlan.posts?.length || 0} />
                  <Stat label="Days" value={activePlan.days} />
                  <Stat label="Pace" value={`${activePlan.posts_per_week}/wk`} />
                  <Stat label="Published" value={activePlan.posts?.filter((p) => p.status === "published").length || 0} />
                </div>
              </div>

              {grouped.map((week) => (
                <div key={week.week} className="bg-zinc-950 border border-zinc-800 rounded-sm" data-testid={`week-${week.week}`}>
                  <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
                    <span className="label-eyebrow">Week {week.week}</span>
                    <span className="text-xs text-zinc-500 font-mono">{week.posts.length} posts</span>
                  </div>
                  <ul className="divide-y divide-zinc-900">
                    {week.posts.map((p) => {
                      const s = STATUS_COLORS[p.status] || STATUS_COLORS.planned;
                      return (
                        <li key={p.id} className="px-6 py-4 hover:bg-zinc-900/50 transition-colors" data-testid={`plan-post-${p.id}`}>
                          <div className="flex items-start gap-4">
                            <div className="w-12 flex-shrink-0 text-center">
                              <div className="text-[10px] uppercase tracking-widest text-zinc-600">Day</div>
                              <div className="font-display text-2xl text-zinc-200">{p.day}</div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-zinc-200">{p.title}</span>
                                <Badge variant="outline" className="rounded-sm border-zinc-700 text-zinc-400 text-[10px] uppercase">{p.type}</Badge>
                                <Badge className="rounded-sm" style={{ background: s.bg + "15", color: s.bg, borderColor: s.bg + "40" }}>{s.label}</Badge>
                              </div>
                              <div className="text-xs text-zinc-500 mt-2 flex items-center gap-3 flex-wrap">
                                <span><Calendar className="w-3 h-3 inline mr-1" /> {p.publish_date}</span>
                                <span>· {p.estimated_word_count} words</span>
                                <span>· <Badge variant="outline" className="rounded-sm border-zinc-800 text-zinc-500 text-[10px] uppercase">{p.intent}</Badge></span>
                                <span className="text-[#3377FF]">#{p.primary_keyword}</span>
                              </div>
                              {p.outline?.length > 0 && (
                                <ul className="mt-3 text-xs text-zinc-500 space-y-1">
                                  {p.outline.slice(0, 4).map((o, i) => (
                                    <li key={i} className="flex gap-2"><span className="text-zinc-700">{i + 1}.</span> {o}</li>
                                  ))}
                                </ul>
                              )}
                            </div>
                            <div className="flex flex-col gap-1 flex-shrink-0">
                              <Button asChild size="sm" variant="outline"
                                      className="bg-zinc-950 hover:bg-zinc-900 border-zinc-800 rounded-sm h-8 text-xs"
                                      data-testid={`plan-write-${p.id}`}>
                                <Link to="/blog" state={{ topic: p.title, keyword: p.primary_keyword }}>
                                  <FileText className="w-3 h-3 mr-1" /> Write
                                </Link>
                              </Button>
                              <Select value={p.status || "planned"} onValueChange={(v) => updateStatus(activePlan.id, p.id, v)}>
                                <SelectTrigger className="h-8 bg-zinc-950 border-zinc-800 rounded-sm text-xs" data-testid={`plan-status-${p.id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-[#0B0B0E] border-zinc-800 rounded-sm">
                                  <SelectItem value="planned">Planned</SelectItem>
                                  <SelectItem value="drafted">Drafted</SelectItem>
                                  <SelectItem value="published">Published</SelectItem>
                                  <SelectItem value="skipped">Skipped</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {!activePlan && !loading && (
            <div className="bg-zinc-950 border border-dashed border-zinc-800 rounded-sm p-16 text-center text-zinc-500">
              <Calendar className="w-8 h-8 mx-auto mb-3 text-zinc-700" />
              <p>No plan yet. Generate one above to map out your next month.</p>
            </div>
          )}
        </>
      )}
    </DashboardLayout>
  );
}

function Stat({ label, value }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="font-display text-xl text-zinc-200">{value}</span>
      <span className="text-xs text-zinc-500 uppercase tracking-widest">{label}</span>
    </div>
  );
}

function ProjectGate() {
  return (
    <div className="bg-zinc-950 border border-dashed border-zinc-800 rounded-sm p-16 text-center" data-testid="no-project-gate">
      <Calendar className="w-10 h-10 mx-auto mb-4 text-zinc-700" />
      <h3 className="font-display text-xl mb-2">No project selected</h3>
      <p className="text-zinc-400 mb-6">Create or activate a project to generate a content calendar.</p>
      <Button asChild className="bg-[#0055FF] hover:bg-[#3377FF] rounded-sm">
        <Link to="/onboarding">Run onboarding <ArrowRight className="w-4 h-4 ml-1.5" /></Link>
      </Button>
    </div>
  );
}
